import { environment } from '@environments/environment';
import { enableProdMode } from '@angular/core';
import { createApplication } from '@angular/platform-browser';
import { publicApplicationConfig } from '@app/bootstrap/public-application.config';
import { AppInitializedEmitterService } from '@shared/services/app-initialized-emitter.service';
import { IFBWindow } from '@app/models/window-namespace';
import { AppRunService } from '@app/app-run';
import { PageTitleService } from '@shared/services/page-title.service';
import { UIRouter, UrlService } from '@uirouter/core';
import { delay, first } from 'rxjs';
import browserHelper from '@utils/browser-helper';
import { AuthService } from '@shared/services/auth.service';
import { blankShield } from '@utils/blankshield';
import { AppPublicComponent } from '@app/app-public.component';

if (environment.production) {
  enableProdMode();
}

async function bootstrapPublicApp() {
  const appRef = await createApplication(publicApplicationConfig);

  const appInitializedEmitterSrv = appRef.injector.get(AppInitializedEmitterService);
  const router = appRef.injector.get(UIRouter);
  const window = appRef.injector.get(IFBWindow);
  const appRunService = appRef.injector.get(AppRunService);
  const pageTitle = appRef.injector.get(PageTitleService);
  const urlService = appRef.injector.get(UrlService);
  const authService = appRef.injector.get(AuthService);

  setUrlProviderRules(urlService, authService, window);
  appRunService.onInit();
  appInitializedEmitterSrv
    .isAppInitialized()
    .pipe(first(), delay(0))
    .subscribe(() => syncRouting(router));
  pageTitle.init();
  blankShield.init();
  appRunService.onBootstrap();
  appInitializedEmitterSrv.markAppAsInitialized();
  appRef.bootstrap(AppPublicComponent);
}

function syncRouting(router: UIRouter) {
  router.urlService.listen();
  router.urlService.sync();
}

function setUrlProviderRules(urlService: UrlService, authService: AuthService, window: IFBWindow) {
  urlService.rules.when('/login', () => {
    // Uses restoreSession instead of LOGIN_LINK (see '/' rule below), since a bare /site/login redirect is missing required OAuth2 params.
    const subdomain = browserHelper.subdomain();

    if (subdomain) {
      authService.restoreSession('/dashboard/');
    }
  });

  urlService.rules.when('/', () => {
    // forces page reload for backend served login page
    const subdomain = browserHelper.subdomain();

    if (subdomain) {
      const search = window.location.search ? '&' + window.location.search.substring(1) : '';
      // Attempt to restore auth session
      authService.LOGIN_LINK.subscribe((loginLink: string) => {
        window.location.href = `${loginLink}?prompt=none${search}`;
      });
    } else {
      // Visiting a general website without a company, redirect to subdomain enter screen
      window.location.href = '/login';
    }
  });
}

bootstrapPublicApp();
